import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUpload, FaFilePdf, FaTrash, FaEye, FaEdit, FaPlus, FaSave, FaTimes } from 'react-icons/fa';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { samples as initialSamples } from '../../data/samples';

const PDFUpload = () => {
  const [samples, setSamples] = useLocalStorage('tinytimes-samples', initialSamples);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newSample, setNewSample] = useState({
    title: '',
    edition: 'micro',
    description: '',
    pdfUrl: null,
  });

  // Handle file upload
  const handleFileUpload = (sampleId, file) => {
    if (file && file.type === 'application/pdf') {
      const url = URL.createObjectURL(file);
      
      if (sampleId === 'new') {
        setNewSample(prev => ({
          ...prev,
          pdfUrl: url,
          fileName: file.name,
          fileSize: file.size,
        }));
      } else {
        setSamples(prev => prev.map(s => 
          s.id === sampleId 
            ? { ...s, pdfUrl: url, fileName: file.name, fileSize: file.size }
            : s
        ));
      }
    } else {
      alert('Please upload a PDF file');
    }
  };

  // Add new sample
  const handleAddSample = () => {
    if (!newSample.title.trim()) {
      alert('Please enter a title');
      return;
    }

    const sample = {
      id: Date.now(),
      ...newSample,
      uploadDate: new Date().toISOString(),
    };

    setSamples(prev => [...prev, sample]);
    setNewSample({
      title: '',
      edition: 'micro',
      description: '',
      pdfUrl: null,
    });
    setIsAddingNew(false);
  };

  // Delete sample
  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this sample?')) {
      setSamples(prev => prev.filter(s => s.id !== id));
    }
  };

  // Update sample
  const handleUpdate = (id, updates) => {
    setSamples(prev => prev.map(s => 
      s.id === id ? { ...s, ...updates } : s
    ));
    setEditingId(null);
  };

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-display font-bold text-primary-green-dark">
          PDF Sample Management
        </h2>
        <motion.button
          onClick={() => setIsAddingNew(true)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="btn-primary flex items-center gap-2"
        >
          <FaPlus />
          <span>Add New Sample</span>
        </motion.button>
      </div>

      {/* Add New Sample Form */}
      <AnimatePresence>
        {isAddingNew && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="card bg-accent-blue/10 border-2 border-primary-green/30"
          >
            <h3 className="text-xl font-display font-bold text-primary-green-dark mb-4">
              Add New Sample
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block font-semibold text-primary-green-dark mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={newSample.title}
                  onChange={(e) => setNewSample(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Micro Edition - Week 5"
                  className="w-full px-4 py-3 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none"
                />
              </div>
              
              <div>
                <label className="block font-semibold text-primary-green-dark mb-2">
                  Edition
                </label>
                <select
                  value={newSample.edition}
                  onChange={(e) => setNewSample(prev => ({ ...prev, edition: e.target.value }))}
                  className="w-full px-4 py-3 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none bg-white"
                >
                  <option value="micro">🌈 Micro Edition (4-9 years)</option>
                  <option value="macro">🚀 Macro Edition (10-15 years)</option>
                </select>
              </div>
            </div>

            <div className="mb-4">
              <label className="block font-semibold text-primary-green-dark mb-2">
                Description
              </label>
              <textarea
                value={newSample.description}
                onChange={(e) => setNewSample(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of this issue..."
                rows={3}
                className="w-full px-4 py-3 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none resize-none"
              />
            </div>

            {/* PDF Upload */}
            <div className="mb-6">
              <label className="block font-semibold text-primary-green-dark mb-2">
                PDF File
              </label>
              <div className="border-2 border-dashed border-primary-green/30 rounded-lg p-8 text-center hover:border-primary-green transition-colors">
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => handleFileUpload('new', e.target.files[0])}
                  className="hidden"
                  id="new-pdf-upload"
                />
                <label htmlFor="new-pdf-upload" className="cursor-pointer">
                  {newSample.pdfUrl ? (
                    <div className="flex items-center justify-center gap-3">
                      <FaFilePdf className="text-4xl text-red-500" />
                      <div className="text-left">
                        <div className="font-semibold text-primary-green-dark">
                          {newSample.fileName}
                        </div>
                        <div className="text-sm text-neutral-gray-dark">
                          Click to change file
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <FaUpload className="text-4xl text-primary-green mx-auto mb-2" />
                      <div className="font-semibold text-primary-green-dark">
                        Click to upload PDF
                      </div>
                      <div className="text-sm text-neutral-gray-dark">
                        or drag and drop
                      </div>
                    </>
                  )}
                </label>
              </div>
            </div>

            <div className="flex gap-3">
              <motion.button
                onClick={handleAddSample}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="btn-primary flex items-center gap-2"
              >
                <FaSave />
                <span>Save Sample</span>
              </motion.button>
              <motion.button
                onClick={() => {
                  setIsAddingNew(false);
                  setNewSample({ title: '', edition: 'micro', description: '', pdfUrl: null });
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-button font-semibold flex items-center gap-2"
              >
                <FaTimes />
                <span>Cancel</span>
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Samples List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {samples.map((sample) => (
          <motion.div
            key={sample.id}
            layout
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="card hover:shadow-card-hover transition-all"
          >
            {editingId === sample.id ? (
              // Edit Mode
              <EditSampleForm
                sample={sample}
                onSave={(updates) => handleUpdate(sample.id, updates)}
                onCancel={() => setEditingId(null)}
                onFileUpload={(file) => handleFileUpload(sample.id, file)}
              />
            ) : (
              // View Mode
              <div>
                <div className="flex items-start gap-4">
                  {/* Thumbnail */}
                  <div className="w-20 h-28 bg-gradient-to-br from-accent-blue to-primary-green-light rounded-lg flex items-center justify-center flex-shrink-0">
                    {sample.pdfUrl ? (
                      <FaFilePdf className="text-3xl text-white" />
                    ) : (
                      <span className="text-4xl">📄</span>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                          sample.edition === 'micro' 
                            ? 'bg-accent-blue text-white' 
                            : 'bg-accent-orange text-white'
                        }`}>
                          {sample.edition === 'micro' ? '🌈 Micro' : '🚀 Macro'}
                        </span>
                        <h3 className="text-lg font-display font-bold text-primary-green-dark mt-2">
                          {sample.title}
                        </h3>
                      </div>
                    </div>

                    <p className="text-sm text-neutral-gray-dark mt-2 line-clamp-2">
                      {sample.description || 'No description provided'}
                    </p>

                    {sample.uploadDate && (
                      <div className="text-xs text-neutral-gray-dark mt-2">
                        Uploaded: {new Date(sample.uploadDate).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 mt-4 pt-4 border-t border-gray-100">
                  {sample.pdfUrl && (
                    <motion.a
                      href={sample.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="flex items-center gap-2 px-3 py-2 bg-primary-green/10 text-primary-green rounded-lg font-semibold text-sm hover:bg-primary-green/20"
                    >
                      <FaEye />
                      <span>View</span>
                    </motion.a>
                  )}
                  <motion.button
                    onClick={() => setEditingId(sample.id)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex items-center gap-2 px-3 py-2 bg-accent-orange/10 text-accent-orange rounded-lg font-semibold text-sm hover:bg-accent-orange/20"
                  >
                    <FaEdit />
                    <span>Edit</span>
                  </motion.button>
                  <motion.button
                    onClick={() => handleDelete(sample.id)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex items-center gap-2 px-3 py-2 bg-red-100 text-red-500 rounded-lg font-semibold text-sm hover:bg-red-200"
                  >
                    <FaTrash />
                    <span>Delete</span>
                  </motion.button>
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {samples.length === 0 && (
        <div className="text-center py-20">
          <div className="text-8xl mb-4">📭</div>
          <h3 className="text-2xl font-display font-bold text-primary-green-dark mb-2">
            No samples yet
          </h3>
          <p className="text-neutral-gray-dark mb-4">
            Upload your first PDF sample to get started!
          </p>
        </div>
      )}
    </div>
  );
};

// Edit Sample Form Component
const EditSampleForm = ({ sample, onSave, onCancel, onFileUpload }) => {
  const [formData, setFormData] = useState({
    title: sample.title,
    edition: sample.edition,
    description: sample.description || '',
  });

  return (
    <div className="space-y-4">
      <input
        type="text"
        value={formData.title}
        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
        className="w-full px-4 py-2 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none font-semibold"
      />
      
      <select
        value={formData.edition}
        onChange={(e) => setFormData(prev => ({ ...prev, edition: e.target.value }))}
        className="w-full px-4 py-2 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none bg-white"
      >
        <option value="micro">🌈 Micro Edition</option>
        <option value="macro">🚀 Macro Edition</option>
      </select>

      <textarea
        value={formData.description}
        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
        placeholder="Description..."
        rows={2}
        className="w-full px-4 py-2 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none resize-none"
      />

      <div>
        <input
          type="file"
          accept=".pdf"
          onChange={(e) => onFileUpload(e.target.files[0])}
          className="hidden"
          id={`edit-pdf-${sample.id}`}
        />
        <label
          htmlFor={`edit-pdf-${sample.id}`}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-green/10 text-primary-green rounded-lg cursor-pointer hover:bg-primary-green/20"
        >
          <FaUpload />
          <span>Replace PDF</span>
        </label>
      </div>

      <div className="flex gap-2">
        <motion.button
          onClick={() => onSave(formData)}
          whileHover={{ scale: 1.02 }}
          className="flex items-center gap-2 px-4 py-2 bg-primary-green text-white rounded-lg font-semibold"
        >
          <FaSave />
          <span>Save</span>
        </motion.button>
        <motion.button
          onClick={onCancel}
          whileHover={{ scale: 1.02 }}
          className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold"
        >
          <FaTimes />
          <span>Cancel</span>
        </motion.button>
      </div>
    </div>
  );
};

export default PDFUpload;